import { ChangeDetectorRef, ViewRef } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { UserTypes } from '../components/profile/profile.enum';
import FieldsSettings from '../forms/fields/fields.settings';
import { CommentView } from '../interfaces/posts.interface';
import { ProfileInfo } from '../interfaces/profile.interface';
import { SearchState } from '../interfaces/search.interface';
import { FullUser, LocationObject, OrganizationType, OrganizationTypeKind } from '../swagger-api';
import { MaybeNull, MaybeUndefined } from '../utils';
import AppSettings from './app.settings';
import AppValues from './app.values';
import { ValueTypes } from './custom.types';

export function defineUserType(user: FullUser): UserTypes {
  if (user.organization) {
    return UserTypes.ORGANIZATION;
  }

  if (user.individual) {
    return UserTypes.INDIVIDUAL;
  }

  return UserTypes.NONE;
}

export function joinFields<T>(objects: T[], delimiter: string = AppSettings.FORM_FIELD_ARRAY_VALUE_SEPARATOR): string {
  return objects.filter(Boolean).join(delimiter);
}

/**
 * @description Analyzes text by pattern and fetched reply username entries.
 *    E.g., for '@test_user: test comment' will return ['@test_user:', 'test_user'].
 * @param {string} text Original comment or message.
 * @param {RegExp} pattern Used pattern. By default usernameReplyComment pattern is used.
 * @returns {string[]}
 */
function getReplyUsernameEntryPattern(
  text: string,
  pattern: RegExp = AppValues.usernameReplyCommentPattern,
): MaybeNull<string[]> {
  return pattern.exec(text);
}

/**
 * @description Returns username if entries is not null.
 *   E.g., for '@test_user: test comment' will return 'test_user'.
 * @param {string} text Original comment or message.
 * @returns {string[]}
 */
export function getReplyUsername(text: string): string {
  const entries: MaybeNull<string[]> = getReplyUsernameEntryPattern(text);

  return entries?.at(1) ?? '';
}

/**
 * @description Returns dates is expired or no.
 * @param {string} endDate Date.
 * @returns {boolean}
 */
export function isDateLaterNow(endDate: string): boolean {
  const nowWithoutTimestamp: number = new Date().setHours(0, 0, 0, 0);
  return new Date(nowWithoutTimestamp) > new Date(endDate);
}

/**
 * @description Returns username entry if entries is not null.
 *    E.g., for '@test_user: test comment' will return '@test_user:'.
 * @param {string} text Original comment or message.
 * @returns {string}
 */
export function getReplyUsernameEntry(text: string): string {
  const entries: MaybeNull<string[]> = getReplyUsernameEntryPattern(text);
  return entries?.at(1) ?? '';
}

/**
 * @description Fetches file extension from file name.
 *    E.g., for 'test_file.png' will return '.png'.
 * @param { string } fileName
 * @returns {string}
 */
export function getExtensionFromFileName(fileName: string): string {
  return fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
}

/**
 * @description Checks file's extension by its content-type or name.
 * @param { File } file
 * @param { string[] } allowedExtensions List of allowed file's extensions.
 * @param isTypeWithoutDot
 */
export function hasValidFileExtension(file: File, allowedExtensions: string[], isTypeWithoutDot?: boolean): boolean {
  return allowedExtensions.some((extension: string) => {
    return (
      file.type.toLowerCase().includes(isTypeWithoutDot ? extension.substring(1) : extension) ||
      getExtensionFromFileName(file.name) === extension
    );
  });
}

/**
 * @description Checks the link and if it has a protocol.
 *    1. If the link is undefined, the empty link will be returned.
 *    2. If the link has no protocol, the 'https://' will be added
 *       at the beginning of url.
 *    Example: 'www.example.com' -> 'https://example.com'.
 * @param { string } link
 */
export function normalizeWebsiteLink(link?: string): string {
  return link ? (AppValues.httpPattern.test(link) ? link : `https://${link}`) : '';
}

/**
 * @description Returns how many days came from the date you send.
 * calculate by (1000 *ms*, 60 *sec*, 60 *min*, 24 *h*)
 * @param { Date } dateSent
 * @returns { number }
 */
export function daysFromNow(dateSent: Date): number {
  const currentDate: Date = new Date();
  dateSent = new Date(dateSent);

  return Math.floor(
    (Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()) -
      Date.UTC(dateSent.getFullYear(), dateSent.getMonth(), dateSent.getDate())) /
      (1000 * 60 * 60 * 24),
  );
}

/**
 * @description Returns the result of checking if objects are equal.
 * @param { ValueTypes } object1
 * @param { ValueTypes } object2
 * @returns { boolean }
 */
export function deepEqual(object1: ValueTypes, object2: ValueTypes): boolean {
  const keys1: string[] = Object.keys(object1);
  const keys2: string[] = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key] as ValueTypes;
    const val2 = object2[key] as ValueTypes;
    const areObjects: boolean = isObject(val1) && isObject(val2);
    if ((areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
}

/**
 * We use an array as an object to avoid dictionary-mode.
 * If the key did not exist then you add that value as distinct.
 * This will be O(n) where n is the number of objects in array and m is the number of unique values.
 * There is no faster way than O(n) because you must inspect each value at least once.
 * @param params
 */
export function getUniqueArray<T>(params: { array: T[]; distinctKey: string }): T[] {
  return [...new Map(params.array.map((item: T) => [item[params.distinctKey], item])).values()];
}

/**
 * @description Returns the result of checking if the value is an object.
 * @param { ValueTypes } value
 * @returns { boolean }
 */
export function isObject(value: ValueTypes): boolean {
  return value !== null && typeof value === 'object';
}

/**
 * @description Returns image link.
 * @param { string } imageUrl
 * @returns { string }
 */
export function getImageUrl(imageUrl: string = AppSettings.DEFAULT_USER_IMAGE): string {
  return imageUrl;
}

/**
 * @description Replaces all subStr with newSubStr in str.
 * @param { string[] } str
 * @param { string[] } subStr
 * @param { string[] } newSubStr
 * @returns { string }
 */
export function replaceAll(str: string, subStr: string, newSubStr: string): string {
  return str.split(subStr).join(newSubStr);
}

/**
 * @description Returns the current time value in milliseconds.
 * @returns { number }
 */
export function getCurrentTime(): number {
  return new Date().getTime();
}

export const sortingByDate: (a: CommentView, b: CommentView) => number = (a: CommentView, b: CommentView) =>
  new Date(b.source.creation_date).getTime() - new Date(a.source.creation_date).getTime();

/**
 * @description Calculates offset value depend on incoming params.
 * @param { SearchState } previousState
 * @param { SearchState } currentState
 * @param { number } limit
 * @returns { number }
 */
export function calculateOffset(
  previousState: SearchState,
  currentState: SearchState,
  limit: number,
  compareStates = true,
): number {
  if (!currentState.pagination) {
    return -1;
  }

  if (compareStates && !deepEqual(previousState.params, currentState.params)) {
    currentState.pagination.currentPage = 1;
    return 0;
  }

  return limit * (currentState.pagination.currentPage - 1);
}

/**
 * @description Unsubscribes from all subscriptions in case list isn't empty.
 * @param { Subscription[] } subscriptions$
 */
export function unsubscribeAll(subscriptions$: Subscription[]): void {
  if (subscriptions$.length > 0) {
    subscriptions$.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}

/**
 * @description Completes subject in case it exists.
 * @param { Subject<T> } subject$
 */
export function completeSubject<T>(subject$: Subject<T>): void {
  if (subject$) {
    subject$.complete();
  }
}

/**
 * @description Completes all subjects in case list is defined.
 * @param subjects$
 */
// eslint-disable-next-line
export function subjectsComplete(subjects$: Subject<any>[]): void {
  // eslint-disable-next-line
  subjects$.forEach((subject: Subject<any>) => {
    if (subject) {
      subject.complete();
    }
  });
}

/**
 * @description Converts string date into NgbDateStruct.
 * @param { string } date
 * @returns { NgbDateStruct }
 */
export function formatToNgbDate(date: string): NgbDateStruct {
  let year: MaybeUndefined<number>;
  let month: MaybeUndefined<number>;
  let day: MaybeUndefined<number>;

  // When values comes from BE, they come in this format: YYYY-MM-DD
  if (date.includes('-')) {
    [year, month, day] = date.split('-').map((value: string) => Number(value));
  }

  // When values comes from FE forms, they come in this format: MM/DD/YYYY, both should be parsed
  if (date.includes('/')) {
    [month, day, year] = date.split('/').map((value: string) => Number(value));
  }

  return { month, day, year };
}

/**
 * @description Extracts a props names of an passed interface.
 * @param { Record<keyof T, 1> } keys
 * @returns {  (keyof T)[] }
 */
export function interfaceKeys<T>(keys: Record<keyof T, 1>): (keyof T)[] {
  return Object.keys(keys) as (keyof T)[];
}

export const unknownLocation: () => LocationObject = () => ({
  center: { lat: 0, lon: 0 },
  city: 'Unknown',
  country: {
    alpha2: 'XX',
    full_name: 'Unknown',
  },
});

/**
 * @description Checks if location value is dumpy or not.
 * @param { LocationObject } location
 * @returns { boolean }
 */
export function isLocationDefined(location: LocationObject): boolean {
  return deepEqual(location, unknownLocation());
}

/**
 * @description Returns organization type profile info object.
 * @param { OrganizationType } value
 * @returns { ProfileInfo }
 */
export function getOrganizationTypeProfileInfo(value: OrganizationType): ProfileInfo {
  return {
    value:
      value.type === OrganizationTypeKind.Custom
        ? value.custom_organization_type_name
        : `${FieldsSettings.ORGANIZATION_TYPE_PREFIX}${value.type}`,
  };
}

export function generateRandomString(textLength: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength: number = characters.length;
  for (let i = 0; i < textLength; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function scrollToElement(elementId: string): void {
  const element: MaybeNull<HTMLElement> = document.getElementById(elementId);

  if (element) {
    element.classList.add(AppValues.HIGHLIGHTED_CLASS);
    element.scrollIntoView({
      block: AppValues.SCROLL_BLOCK_POSITION,
      behavior: AppValues.SCROLL_BEHAVIOR,
    });

    setTimeout(() => element.classList.remove(AppValues.HIGHLIGHTED_CLASS), AppValues.HIGHLIGHTED_TIME);
  }
}

export function getClientTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function safelyDetectChanges(cdr: ChangeDetectorRef): void {
  if (!(cdr as ViewRef).destroyed) {
    cdr.detectChanges();
  }
}

export function equalSets<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) {
    return false;
  }

  return Array.from(a).every((element: T) => {
    return b.has(element);
  });
}

/**
 * @description Parser text which convert link to html standard.
 * @param { string } plainText
 * @returns { string }
 */

export function linkify(plainText: string): string {
  let replacedText: string;

  if (plainText.includes('</a>')) {
    return plainText;
  }

  replacedText = plainText.replace(AppValues.replacePatternWeb, '<a href="$1" target="_blank">$1</a>');

  replacedText = replacedText.replace(
    AppValues.replacePatternWebHaveWww,
    '$1<a href="http://$2" target="_blank">$2</a>',
  );

  replacedText = replacedText.replace(AppValues.replacePatternMailTo, '<a href="mailto:$1">$1</a>');

  return replacedText;
}

export function extractFileNameFromUrl(url: string): string {
  return decodeURIComponent(new URL(url).pathname.split('/').pop() ?? '');
}

export function isSecurityPage(currentPath: string): boolean {
  return AppValues.SECURITY_PAGES.some((page: string) => currentPath.includes(page));
}

export function changeDeviceView(deviceWidth: number): boolean {
  return window.innerWidth < deviceWidth;
}

export function logError<T>(input: T) {
  console.error(input);
}

export function promiseWithCatch<T>(promise?: Promise<T>): void {
  if (!_.isUndefined(promise)) {
    promise.catch((e) => logError(e));
  }
}

export const objectValue: <T extends object, K extends keyof T>(obj: T, key: K) => T[K] = (obj, key) => obj[key];
